import { spawnSync } from 'node:child_process';

/** Run git with fixed args (shell:false everywhere). Returns null if git is unavailable. */
export function git(args: string[], cwd: string): { ok: boolean; output: string } | null {
  const res = spawnSync('git', args, { cwd, encoding: 'utf8', shell: false });
  if (res.error && (res.error as NodeJS.ErrnoException).code === 'ENOENT') return null;
  return {
    ok: res.status === 0,
    output: `${res.stdout ?? ''}${res.stderr ?? ''}`.trim(),
  };
}

export function gitAvailable(): boolean {
  return git(['--version'], process.cwd()) !== null;
}

/** `git init -b main` + initial commit. Returns a human note; never throws. */
export function gitInitCommit(dir: string, message: string): string {
  if (git(['init', '-b', 'main'], dir) === null) {
    return 'git not found — skipped `git init` (install git and run `git init -b main` later).';
  }
  git(['add', '-A'], dir);
  const commit = git(
    ['-c', 'user.name=ghosty-cli', '-c', 'user.email=cli@ghosty.local', 'commit', '-m', message],
    dir,
  );
  if (!commit?.ok) {
    return `git repository initialized, but the initial commit failed: ${commit?.output ?? 'unknown error'}`;
  }
  return 'git repository initialized on branch main with an initial commit.';
}

/** Best-effort `git remote add origin` + fetch. Never throws. */
export function gitWireRemote(dir: string, remoteUrl: string): string {
  const existing = git(['remote', 'get-url', 'origin'], dir);
  if (existing === null) return 'git not found — add the remote manually.';
  if (existing.ok) {
    return `git remote "origin" already set (${existing.output}).`;
  }
  const add = git(['remote', 'add', 'origin', remoteUrl], dir);
  if (!add?.ok) return `could not add git remote: ${add?.output ?? 'unknown error'}`;
  const fetch = git(['fetch', 'origin'], dir);
  return fetch?.ok
    ? `git remote "origin" set to ${remoteUrl} and fetched.`
    : `git remote "origin" set to ${remoteUrl} (fetch failed: ${fetch?.output ?? 'unknown'}).`;
}
